import { useState, useEffect } from 'react';
import { useNavigate, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Check, Star, Zap, Camera, Shield, Share2, MapPin, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from '../components/ConfirmDialog';
import LanguageSelector from '../components/LanguageSelector';
import fetchWithTimeout from '../lib/fetchWithTimeout';
import { isNativePlatform, isIOS } from '../lib/capacitor';
import { API_URL as BACKEND_URL } from '../lib/apiUrl';
import { Browser } from '@capacitor/browser';

// Stripe Price IDs
const PRICE_IDS = {
  starter_monthly: 'price_1SrRuxFeCOjQuFFwJ09ienR8',
  starter_annual: 'price_1SrRvrFeCOjQuFFwzQAxzjwb',
  pro_monthly: 'price_1SrS0lFeCOjQuFFwJToLNDej',
  pro_annual: 'price_1SrS0lFeCOjQuFFwC6MpBFpl',
};

const PLANS = [
  {
    name: 'Free',
    tier: 'free',
    monthlyPrice: 0,
    annualPrice: 0,
    features: [
      '3 basic lookups per day',
      'License status verification',
      'Company/base information',
      'Active/expired status',
      'Quick Share (text only)',
    ],
    limitations: [
      'No car images',
      'No VIN details',
      'No shareable trip links',
      'No live location tracking',
    ],
  },
  {
    name: 'Starter',
    tier: 'starter',
    monthlyPrice: 19.99,
    annualPrice: 199.99,
    annualSavings: 40,
    popular: true,
    priceIdMonthly: PRICE_IDS.starter_monthly,
    priceIdAnnual: PRICE_IDS.starter_annual,
    features: [
      'Unlimited basic lookups',
      '25 enhanced lookups/month',
      'Car images',
      'Full VIN details',
      'Color verification',
      'Quick Share with photo',
      'Shareable trip links',
    ],
    limitations: [
      'No live location tracking',
    ],
  },
  {
    name: 'Pro',
    tier: 'pro',
    monthlyPrice: 34.99,
    annualPrice: 349.99,
    annualSavings: 70,
    priceIdMonthly: PRICE_IDS.pro_monthly,
    priceIdAnnual: PRICE_IDS.pro_annual,
    features: [
      'Unlimited basic lookups',
      '75 enhanced lookups/month',
      'Car images',
      'Full VIN details',
      'Color verification',
      'Quick Share with photo',
      'Shareable trip links',
      'Live location tracking',
      'Priority support',
    ],
  },
  {
    name: 'Enterprise',
    tier: 'enterprise',
    monthlyPrice: null,
    annualPrice: null,
    enterprise: true,
    features: [
      'Unlimited basic lookups',
      '500+ enhanced lookups/month',
      'All Pro features',
      'API access',
      'Bulk lookups',
      'Dedicated support',
      'Custom invoicing',
    ],
  },
];

export default function Pricing() {
  const { t } = useTranslation();
  const { user, tier, stripePriceId, getAccessToken } = useAuth();
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, plan: null });
  const navigate = useNavigate();

  // Helper to check if this plan/billing combo is the current subscription
  const isCurrentPlan = (plan) => {
    if (!stripePriceId) return false;
    const planPriceId = billingPeriod === 'annual' ? plan.priceIdAnnual : plan.priceIdMonthly;
    return stripePriceId === planPriceId;
  };

  // On native apps, direct users to the website for subscriptions
  // (Apple/Google require their own billing for in-app purchases)
  const openWebsitePricing = async () => {
    const url = 'https://ridesharechecker.com/pricing';
    if (isNativePlatform()) {
      await Browser.open({ url });
    } else {
      window.open(url, '_blank');
    }
  };

  const handleSubscribe = async (plan) => {
    // Native apps can't process payments directly (App Store/Play Store rules)
    if (isNativePlatform()) {
      openWebsitePricing();
      return;
    }

    const priceId = billingPeriod === 'annual' ? plan.priceIdAnnual : plan.priceIdMonthly;

    if (!priceId) {
      toast.error('Price not configured for this plan');
      return;
    }

    // If not logged in, save the plan and redirect to signup
    if (!user) {
      localStorage.setItem('pendingCheckout', JSON.stringify({ priceId, planName: plan.name }));
      navigate(`/login?signup=true&plan=${encodeURIComponent(plan.name)}`);
      return;
    }

    // If user already has a paid subscription, show confirmation dialog
    if (tier !== 'free') {
      setConfirmDialog({ isOpen: true, plan });
      return;
    }

    // New subscriber - use checkout
    await startCheckout(priceId);
  };

  const confirmPlanChange = async () => {
    const plan = confirmDialog.plan;
    setConfirmDialog({ isOpen: false, plan: null });

    if (!plan) return;

    const priceId = billingPeriod === 'annual' ? plan.priceIdAnnual : plan.priceIdMonthly;
    await upgradeSubscription(priceId, plan.name);
  };

  const upgradeSubscription = async (priceId, planName) => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/upgrade-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ price_id: priceId })
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Upgrade failed');

      if (data.success) {
        toast.success(`Successfully changed to ${planName}! Proration applied.`);
        window.location.reload();
      } else {
        toast.error(data.error || 'Could not change subscription. Please try again.');
      }
    } catch (error) {
      toast.error(error.message || 'Error changing subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startCheckout = async (priceId) => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ price_id: priceId })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Checkout failed');

      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.error || 'Could not start checkout. Please try again.');
      }
    } catch (error) {
      if (error.message === 'Not authenticated') {
        localStorage.setItem('pendingCheckout', JSON.stringify({ priceId }));
        navigate('/login');
      } else {
        toast.error(error.message || 'Error starting checkout. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check for pending checkout after login
  useEffect(() => {
    const processPendingCheckout = async () => {
      const pending = localStorage.getItem('pendingCheckout');
      if (pending && user) {
        localStorage.removeItem('pendingCheckout');
        const { priceId } = JSON.parse(pending);
        if (priceId) {
          await startCheckout(priceId);
        }
      }
    };
    processPendingCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (isIOS()) {
    return <Navigate to="/" replace />;
  }

  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    return `$${price.toFixed(2)}`;
  };

  // Determine tier order for upgrade/downgrade display
  const tierOrder = { free: 0, starter: 1, pro: 2 };

  // Check if user is on this tier but different billing period
  const isSameTierDifferentBilling = (plan) => {
    if (!stripePriceId) return false;
    const isOnThisTier = tier === plan.tier;
    const planPriceId = billingPeriod === 'annual' ? plan.priceIdAnnual : plan.priceIdMonthly;
    return isOnThisTier && stripePriceId !== planPriceId;
  };

  // Check if user is on a different billing period than what's displayed
  const isOnDifferentBillingPeriod = () => {
    if (!stripePriceId) return false;
    const currentIsAnnual = stripePriceId.includes('annual') ||
      stripePriceId === PRICE_IDS.starter_annual ||
      stripePriceId === PRICE_IDS.pro_annual;
    return (billingPeriod === 'annual') !== currentIsAnnual;
  };

  const getButtonText = (plan) => {
    if (loading) return t('pricing.pleaseWait');
    if (tier === 'free') return t('pricing.upgrade');

    // Check if switching billing period within same tier
    if (isSameTierDifferentBilling(plan)) {
      return billingPeriod === 'annual' ? 'Switch to Annual' : 'Switch to Monthly';
    }

    const currentOrder = tierOrder[tier] || 0;
    const planOrder = tierOrder[plan.tier] || 0;
    const changingBilling = isOnDifferentBillingPeriod();

    if (planOrder > currentOrder) {
      // If also switching billing period, use neutral text
      return changingBilling ? 'Change Plan' : 'Upgrade';
    } else if (planOrder < currentOrder) {
      // If also switching billing period, use neutral text
      return changingBilling ? 'Change Plan' : 'Downgrade';
    }
    return t('pricing.switchPlan');
  };

  return (
    <div className="app-container">
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Change Subscription?"
        message={confirmDialog.plan ? `You're about to switch to the ${confirmDialog.plan.name} plan (${billingPeriod}). Your billing will be prorated.` : ''}
        confirmText="Yes, Change Plan"
        cancelText="Cancel"
        onConfirm={confirmPlanChange}
        onCancel={() => setConfirmDialog({ isOpen: false, plan: null })}
        variant="warning"
      />
      <div className="app-wrapper" style={{ maxWidth: '1000px' }}>
        <div className="app-card">
          <div className="app-header">
            <div className="header-top">
              <h1>
                <Zap className="header-icon" />
                {t('pricing.title')}
              </h1>
              <div className="header-actions">
                <LanguageSelector />
              </div>
            </div>
            <p>{t('pricing.subtitle')}</p>
          </div>

          <div className="pricing-section">
            {/* Native app notice — subscriptions must be managed on the website */}
            {isNativePlatform() && (
              <div className="native-pricing-notice" style={{
                background: '#fef3c7', borderRadius: '8px', padding: '12px 16px',
                marginBottom: '16px', fontSize: '0.9rem', color: '#92400e',
                display: 'flex', alignItems: 'center', gap: '8px'
              }}>
                <ExternalLink size={18} />
                <span>To subscribe or manage your plan, visit <button onClick={openWebsitePricing} style={{
                  background: 'none', border: 'none', color: '#b45309', textDecoration: 'underline',
                  cursor: 'pointer', padding: 0, font: 'inherit', fontWeight: 600
                }}>ridesharechecker.com/pricing</button></span>
              </div>
            )}

            {/* Billing Toggle */}
            <div className="billing-toggle">
              <button
                className={`toggle-btn ${billingPeriod === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button
                className={`toggle-btn ${billingPeriod === 'annual' ? 'active' : ''}`}
                onClick={() => setBillingPeriod('annual')}
              >
                Annual <span className="save-badge">Save up to $70</span>
              </button>
            </div>

            <div className="pricing-grid">
              {PLANS.map((plan) => (
                <div
                  key={plan.tier}
                  className={`plan-card ${plan.popular ? 'popular' : ''} ${isCurrentPlan(plan) ? 'current' : ''}`}
                >
                  {plan.popular && <div className="popular-badge"><Star size={14} /> {t('pricing.mostPopular')}</div>}

                  <h2>{plan.name}</h2>
                  <div className="plan-price">
                    {plan.enterprise ? 'Custom' : formatPrice(billingPeriod === 'annual' ? plan.annualPrice : plan.monthlyPrice)}
                    {plan.monthlyPrice > 0 && (
                      <span className="price-period">/{billingPeriod === 'annual' ? 'year' : 'month'}</span>
                    )}
                  </div>
                  {billingPeriod === 'annual' && plan.annualSavings && (
                    <div className="annual-savings">Save ${plan.annualSavings}/year</div>
                  )}

                  <ul className="plan-features">
                    {plan.features?.map((feature, i) => (
                      <li key={i}>
                        <Check size={16} className="check-icon" />
                        {feature}
                      </li>
                    ))}
                    {plan.limitations?.map((limitation, i) => (
                      <li key={`lim-${i}`} className="limitation">
                        <span className="x-icon">✕</span>
                        {limitation}
                      </li>
                    ))}
                  </ul>

                  {isCurrentPlan(plan) ? (
                    <div className="current-plan-badge">{t('pricing.currentPlan')}</div>
                  ) : plan.enterprise ? (
                    <a
                      href="mailto:info@wiredcop.com?subject=Enterprise%20Plan%20Inquiry"
                      className="btn btn-dark"
                    >
                      Contact Us
                    </a>
                  ) : plan.priceIdMonthly && (
                    <button
                      onClick={() => handleSubscribe(plan)}
                      disabled={loading}
                      className={`btn ${plan.popular ? 'btn-yellow' : 'btn-dark'}`}
                    >
                      {getButtonText(plan)}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* What's included explanation */}
            <div className="pricing-explainer">
              <h3>What's the difference?</h3>
              <div className="explainer-grid">
                <div className="explainer-item">
                  <Shield size={24} />
                  <h4>Basic Lookup</h4>
                  <p>Verify if a vehicle is licensed. Shows license type, company, and active status. Free for all users.</p>
                </div>
                <div className="explainer-item">
                  <Camera size={24} />
                  <h4>Enhanced Lookup</h4>
                  <p>Adds car photo, VIN details, and color verification. Helps confirm you're getting in the right car.</p>
                </div>
                <div className="explainer-item">
                  <Share2 size={24} />
                  <h4>Share My Ride</h4>
                  <p>Send trip details to friends and family. Subscribers can share with photo and create shareable links.</p>
                </div>
                <div className="explainer-item">
                  <MapPin size={24} />
                  <h4>Live Tracking</h4>
                  <p>Pro feature: Share your real-time location on a map so others can follow your trip as it happens.</p>
                </div>
              </div>
              <div className="coverage-note">
                <strong>Coverage:</strong> License verification available for <span className="city-tag">NYC</span> and <span className="city-tag">Chicago</span>.
                Share My Ride works in all 50 states for your safety.
              </div>
            </div>

            <div className="pricing-footer">
              <Link to="/" className="back-link">{t('pricing.backToApp')}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
